import { Loading } from '@postidigital/posti-components'
import { observer } from 'mobx-react-lite'
import React, { ComponentPropsWithRef, FC, useCallback, useRef, useState } from 'react'
import styled from 'styled-components'

import { ResponsiveLink } from '../typography/ResponsiveLink'

/**
 * A button that disables itself for a given amount of time after being clicked.
 * @param delay
 * @param onClick
 * @param children
 * @param props
 * @constructor
 */
export const DelayLinkButton: FC<
  Omit<ComponentPropsWithRef<typeof ResponsiveLink>, 'onClick'> & {
    delay: number
    onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void | Promise<void>
    isLoading?: boolean
  }
> = observer(({ delay, onClick, isLoading, children, ...props }) => {
  const [linkDisabled, setLinkDisabled] = useState<boolean>(!!props.disabled)
  const timeout = useRef<NodeJS.Timeout | null>(null)

  const resetDisabledState = useCallback(() => {
    setLinkDisabled(!!props.disabled)

    if (timeout?.current) {
      clearTimeout(timeout.current)
    }
  }, [props.disabled])

  const handleClick = useCallback(
    async (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (linkDisabled) {
        return
      }

      setLinkDisabled(true)

      try {
        await onClick?.(event)
      } catch (e) {
        console.error(e)
      } finally {
        timeout.current = setTimeout(resetDisabledState, delay)
      }
    },
    [delay, linkDisabled, onClick, resetDisabledState]
  )

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <ResponsiveLink
        onClick={handleClick}
        {...props}
        style={{
          ...(props.style || {}),
          pointerEvents: linkDisabled ? 'none' : 'auto',
          color: linkDisabled ? 'inherit' : props.style?.color,
        }}
      >
        {children || null}
      </ResponsiveLink>
      {isLoading && (
        <span style={{ marginLeft: '1rem' }}>
          <Loading size="xs" />
        </span>
      )}
    </div>
  )
})

export const LinkButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`
